import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle2, XCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import PortalShell from '@/components/portal/PortalShell';
import { GiisdecMark } from '@/components/portal/BrandLogo';
import { authApi } from '@/lib/authApi';
import { Card, Button } from '@/components/eds';

/**
 * Public Password Reset page — /reset-password?token=…
 *
 * Security posture:
 *  - Reset token is read from the query string, held only in transient memory,
 *    never logged, never persisted, and cleared from state after completion.
 *  - Invalid / expired tokens show a friendly message with a link to request a
 *    new reset — no token details or backend exceptions are surfaced.
 *  - Frontend guidance mirrors (never weakens) backend password policy; the
 *    backend remains authoritative.
 *  - Endpoint resolves to {BASE}/api/auth/reset-password (never /api/api).
 *
 * WP2.1B: presentation migrated to EDS v1.0. All token/phase/policy logic
 * is unchanged; inputs keep their show/hide toggles (restyled to EDS tokens).
 */

const RULES: { id: string; label: string; test: (v: string) => boolean }[] = [
  { id: 'len', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { id: 'upper', label: 'An uppercase letter (A–Z)', test: (v) => /[A-Z]/.test(v) },
  { id: 'lower', label: 'A lowercase letter (a–z)', test: (v) => /[a-z]/.test(v) },
  { id: 'digit', label: 'A number (0–9)', test: (v) => /\d/.test(v) },
  { id: 'special', label: 'A special character (!@#$…)', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  // Accept both `token` and `resetToken` query conventions.
  const token = params.get('token') || params.get('resetToken') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'success' | 'invalid-token'>(token ? 'form' : 'invalid-token');
  const [loading, setLoading] = useState(false);

  const ruleResults = useMemo(() => RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
  const allRulesOk = ruleResults.every((r) => r.ok);
  const mismatch = confirm.length > 0 && password !== confirm;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!allRulesOk) {
      setError('Please choose a password that meets all the requirements below.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match. Please re-enter them.');
      return;
    }
    setLoading(true);
    const res = await authApi.resetPassword(token, password);
    setLoading(false);

    if (res.error) {
      // Friendly handling only — never expose token details or raw backend errors.
      if (res.status === 400 || res.status === 401 || res.status === 403 || res.status === 404 || res.status === 410) {
        setPhase('invalid-token');
      } else {
        setError('We could not reset your password right now. Please try again shortly.');
      }
      return;
    }

    // Clear recovery state from frontend memory.
    setPassword('');
    setConfirm('');
    setPhase('success');
  };

  const inputCls =
    'w-full pl-9 pr-10 py-2 border border-eds-border rounded-eds-md text-sm bg-eds-surface text-eds-fg focus:outline-none focus:ring-4 focus:ring-eds-ring/20 focus:border-eds-emerald';

  return (
    <PortalShell>
      <section className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-eds-bg py-12 px-4 font-eds-body">
        <Card className="w-full max-w-md p-8">
          <div className="flex items-center gap-2.5 mb-6">
            <GiisdecMark className="w-8 h-8 flex-shrink-0" />
            <div className="leading-tight">
              <div className="font-eds-display font-bold text-sm tracking-tight text-eds-fg">GIISDEC</div>
              <div className="text-[9px] uppercase tracking-widest text-eds-muted-fg">Investor Portal</div>
            </div>
          </div>
          {phase === 'form' && (
            <>
              <h1 className="font-eds-display text-2xl font-bold text-eds-fg">Set a new password</h1>
              <p className="text-sm text-eds-muted-fg mt-1 mb-6">
                Choose a new password for your GIISDEC portal account.
              </p>

              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="rp-password" className="text-[13px] font-semibold text-eds-fg block mb-1">
                    New password
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-3 text-eds-muted-fg" aria-hidden="true" />
                    <input
                      id="rp-password"
                      type={showPw ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      aria-describedby="rp-rules"
                      className={inputCls}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      aria-pressed={showPw}
                      className="absolute right-2 top-2 p-1 rounded-eds-md text-eds-muted-fg hover:text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="rp-confirm" className="text-[13px] font-semibold text-eds-fg block mb-1">
                    Confirm new password
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-3 text-eds-muted-fg" aria-hidden="true" />
                    <input
                      id="rp-confirm"
                      type={showConfirm ? 'text' : 'password'}
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      aria-describedby={mismatch ? 'rp-mismatch' : undefined}
                      aria-invalid={mismatch || undefined}
                      className={inputCls}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((s) => !s)}
                      aria-label={showConfirm ? 'Hide confirmation password' : 'Show confirmation password'}
                      aria-pressed={showConfirm}
                      className="absolute right-2 top-2 p-1 rounded-eds-md text-eds-muted-fg hover:text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                    </button>
                  </div>
                  {mismatch && (
                    <p id="rp-mismatch" role="alert" className="mt-1 text-xs text-eds-danger">
                      Passwords do not match.
                    </p>
                  )}
                </div>

                {/* Password guidance — mirrors backend policy; backend remains authoritative. */}
                <div id="rp-rules" className="bg-eds-surface-2 border border-eds-border rounded-eds-md p-3">
                  <p className="text-xs font-medium text-eds-fg mb-2">Your password must include:</p>
                  <ul className="space-y-1">
                    {ruleResults.map((r) => (
                      <li key={r.id} className={`flex items-center gap-2 text-xs ${r.ok ? 'text-eds-emerald-2' : 'text-eds-muted-fg'}`}>
                        {r.ok
                          ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                          : <XCircle className="w-3.5 h-3.5 shrink-0 text-eds-muted-fg" aria-hidden="true" />}
                        <span>
                          {r.label}
                          <span className="sr-only">{r.ok ? ' — requirement met' : ' — requirement not yet met'}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-eds-muted-fg mt-2">Avoid common or previously used passwords.</p>
                </div>

                {error && (
                  <div role="alert" className="text-sm text-eds-danger bg-eds-danger/8 border border-eds-danger/30 rounded-eds-md p-3">
                    {error}
                  </div>
                )}

                <Button type="submit" block loading={loading}>
                  {loading ? 'Resetting…' : 'Reset password'}
                </Button>
              </form>
            </>
          )}

          {phase === 'success' && (
            <div role="status" aria-live="polite">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-eds-pill bg-eds-success/12 border border-eds-success/30">
                  <ShieldCheck className="w-5 h-5 text-eds-emerald-2" aria-hidden="true" />
                </span>
                <h1 className="font-eds-display text-xl font-bold text-eds-fg">Password reset successful</h1>
              </div>
              <p className="text-sm text-eds-fg">
                Your password has been reset. You can now sign in with your new password.
              </p>
              <Link
                to="/investor-login"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg rounded-eds-md font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
              >
                Go to Sign In
              </Link>
            </div>
          )}

          {phase === 'invalid-token' && (
            <div role="status" aria-live="polite">
              <h1 className="font-eds-display text-xl font-bold text-eds-fg">This reset link is not valid</h1>
              <p className="text-sm text-eds-fg mt-2">
                The password reset link is invalid, has expired, or has already been used.
                For your security, reset links can only be used once and expire after a
                limited time.
              </p>
              <Link
                to="/forgot-password"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg rounded-eds-md font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
              >
                Request a new reset link
              </Link>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-eds-border text-sm">
            <Link to="/investor-login" className="inline-flex items-center gap-1.5 text-eds-emerald-2 font-medium hover:underline">
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Return to Sign In
            </Link>
          </div>
        </Card>
      </section>
    </PortalShell>
  );
};

export default ResetPassword;
